import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Stack,
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress,
  Switch,
  Avatar,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  EmailRounded,
  PhoneRounded,
  GroupsRounded,
  SmsRounded,
  MoreTimeRounded,
} from "@mui/icons-material";
import client from "../../../../API";

const methodIcons = {
  email: <EmailRounded fontSize="small" />,
  call: <PhoneRounded fontSize="small" />,
  meeting: <GroupsRounded fontSize="small" />,
  sms: <SmsRounded fontSize="small" />,
  other: <MoreTimeRounded fontSize="small" />,
};

// Helper: pad numbers to two digits
const pad = (n) => n.toString().padStart(2, "0");

// Convert a Date (or now) to the "yyyy-MM-dd'T'HH:mm" string for <input type="datetime-local">
const toLocalInput = (d = new Date()) => {
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Convert a "yyyy-MM-ddTHH:mm" (local) string to an ISO UTC string with "+00:00"
const toIsoUtc = (localValue) => {
  const dt = new Date(localValue);
  if (isNaN(dt.getTime())) return null;
  // toISOString() gives "YYYY-MM-DDTHH:mm:ss.sssZ"; replace "Z" with "+00:00"
  return dt.toISOString().replace("Z", "+00:00");
};

// Check if a value is a valid Date
const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

// Format a Date for display as "MMM d, yyyy at h:mm a"
const formatDisplayDate = (d) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = pad(d.getMinutes());
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
};

// Compute a simple "time ago" string, e.g. "3 days ago"
const relativeTimeFromNow = (pastDate) => {
  const now = new Date();
  let delta = Math.floor((now.getTime() - pastDate.getTime()) / 1000); // in seconds
  const future = delta < 0;
  delta = Math.abs(delta);

  const units = [
    { name: "year", secs: 31536000 },
    { name: "month", secs: 2592000 },
    { name: "day", secs: 86400 },
    { name: "hour", secs: 3600 },
    { name: "minute", secs: 60 },
    { name: "second", secs: 1 },
  ];

  for (const unit of units) {
    const count = Math.floor(delta / unit.secs);
    if (count >= 1) {
      const unitName = count > 1 ? `${unit.name}s` : unit.name;
      return future
        ? `in ${count} ${unitName}`
        : `${count} ${unitName} ago`;
    }
  }
  return "just now";
};

const DialogShell = ({ title, onClose, icon, children }) => {
  const theme = useTheme();

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          background: theme.palette.background.default,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.common.white,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: theme.palette.common.white,
              width: 32,
              height: 32,
            }}
          >
            {React.cloneElement(icon, {
              sx: { color: theme.palette.primary.main },
            })}
          </Avatar>
          <Typography variant="h6" fontWeight={600} letterSpacing={0.5}>
            {title}
          </Typography>
          <Box flexGrow={1} />
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.common.white,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      {children}
    </Dialog>
  );
};

export default function ClientNotesDialog({
  popup,
  setPopup,
  refreshClients,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const clientId = popup?.client?.client_id ?? popup?.client?.id;

  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isLast, setIsLast] = useState(false);
  const [method, setMethod] = useState("email");
  const [date, setDate] = useState(toLocalInput());

  const fetchNotes = useCallback(async () => {
    if (!clientId) return;
    try {
      const { notes: apiNotes = [] } =
        await client.users.getClientNotes(clientId);
      setNotes(apiNotes);
    } catch (err) {
      console.error("[ClientNotesDialog] fetch error", err);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const close = () => setPopup({ type: null });

  const addNote = async () => {
    try {
      const payload = {
        body: newNote.trim(),
        category: isLast ? "last_contact" : "general",
        contact_method: isLast ? method : undefined,
        contact_date: isLast ? toIsoUtc(date) : undefined,
      };

      const created = await client.users.postClientNote(clientId, payload);
      setNotes((prev) => [created, ...prev]);
      setNewNote("");
      setIsLast(false);
      setMethod("email");
      setDate(toLocalInput());
      if (isLast) refreshClients?.();
    } catch (err) {
      console.error("[ClientNotesDialog] add error", err);
    }
  };

  return (
    <DialogShell
      title={`${popup.client?.full_name ?? "Client"} Notes`}
      icon={<EditIcon />}
      onClose={close}
    >
      <DialogContent
        sx={{
          p: 0,
          height: isMobile ? "70vh" : "60vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Notes List */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={80}
                sx={{ mb: 2, borderRadius: 2 }}
              />
            ))
          ) : notes.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 300 }}
              >
                No notes found. Start by adding your first note below.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {notes.map((n) => {
                const parsedDate = n.contact_date
                  ? new Date(n.contact_date)
                  : null;
                const dateValid = parsedDate && isValidDate(parsedDate);

                return (
                  <Box
                    key={n.id}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                      borderLeft: `4px solid ${
                        n.category === "last_contact"
                          ? theme.palette.success.main
                          : theme.palette.divider
                      }`,
                      boxShadow: theme.shadows[1],
                      transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
                      "&:hover": {
                        transform: "translateX(4px)",
                        boxShadow: theme.shadows[3],
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      whiteSpace="pre-line"
                      sx={{ lineHeight: 1.6, mb: 1 }}
                    >
                      {n.body}
                    </Typography>

                    {n.category === "last_contact" && dateValid && (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mt: 1.5 }}
                      >
                        {methodIcons[n.contact_method] ||
                          methodIcons.other}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span style={{ textTransform: "capitalize" }}>
                            {n.contact_method}
                          </span>
                          • {formatDisplayDate(parsedDate)}
                          <Chip
                            label={relativeTimeFromNow(parsedDate)}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              fontSize: "0.65rem",
                              height: 20,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* New Note Form */}
        <Box
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack spacing={2}>
            <TextField
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              size="medium"
              placeholder="Compose new note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.light,
                  },
                },
              }}
            />

            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isLast}
                    onChange={(e) =>
                      setIsLast(e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    Mark as last contact
                  </Typography>
                }
              />

              {isLast && (
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={2}
                  alignItems={isMobile ? "stretch" : "center"}
                  sx={{ flex: 1 }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel>Method</InputLabel>
                    <Select
                      value={method}
                      label="Method"
                      onChange={(e) =>
                        setMethod(e.target.value)
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      {Object.entries(methodIcons).map(
                        ([key, icon]) => (
                          <MenuItem key={key} value={key}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {icon}
                              <Typography textTransform="capitalize">
                                {key}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Date & Time"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <Tooltip title="Set to current time" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setDate(toLocalInput())}
                            sx={{ mr: -1 }}
                          >
                            <CalendarIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Stack>
              )}
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={2}
              sx={{ pt: 1 }}
            >
              <Button
                variant="text"
                onClick={close}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  px: 4,
                  borderRadius: 2,
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    boxShadow: theme.shadows[4],
                    transform: "translateY(-1px)",
                  },
                }}
                disabled={!newNote.trim()}
                onClick={addNote}
              >
                Save Note
              </Button>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
    </DialogShell>
  );
}
